import { DateInputField, DateInputFieldType, DropdownButtonType,List, ListItem,LocallyFilteringList,SingleSelectDropdown, TextFieldWidget } from '@tableau/widgets';
import { DateFormat } from '@tableau/widgets/lib/src/widgets/DateInputField';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { classes,cssClass } from '~src/style';

import { FlowParameter } from '../types/FlowParameters';
import { getFlowParameterInputPlaceholder,getIconForParameterType } from '../utils/FlowParametersUtils';

const rootClass = 'FlowParamterInput';

const iconPadding = {
  paddingLeft: 30,
};

const wrapper = cssClass(rootClass, 'wrapper', {
  marginTop: 12,
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  $nest: {
    '& > div': {
      flex: '1 1 auto',
    }
  }
});
const inputPadding = cssClass(rootClass, 'inputPadding', {
  $nest: {
    '& input': {
      paddingLeft: 30,
    }
  }
});
const typeIcon = cssClass(rootClass, 'typeIcon', {
  position: 'absolute',
  zIndex: 1,
  marginLeft: 8,
  pointerEvents: 'none',
  top: 4,
});

export interface FlowParameterInputProps {
  parameter: FlowParameter;
  value: string;
  updateValue: (id: string, value: string) => void;
}

export function FlowParameterInput(props: FlowParameterInputProps) {

  const memoizedDate = React.useMemo(() => {
    if (props.parameter.domain.type === 'date' || props.parameter.domain.type === 'datetime') {
      // Create date object from the unix epoch string, must be cast to number for valid date object
      const date: Date = props.value ? new Date(Number(props.value)) : new Date();
      // Check if the generated date is valid
      return isNaN(date.getTime()) ? new Date() : date;
    } else {
      return undefined;
    }
  }, [props.value, props.parameter.domain.type]);

  function handleDateChange(value: Date) {
    if (value) {
      props.updateValue(props.parameter.id, value.getTime().toString());
    }
  }

  function handleValueChange(e: React.ChangeEvent<HTMLInputElement>) {
    props.updateValue(props.parameter.id, e.target.value);
  }

  function handleBooleanChange(value: string) {
    props.updateValue(props.parameter.id, value);
  }

  function renderBooleanInput() {
    const listItems: ListItem[] = [{
      value: 'true',
      content: Messages.flowParametersInputBooleanTrue(),
    }, {
      value: 'false',
      content: Messages.flowParametersInputBooleanFalse(),
    }];
    const list = <List items={listItems} style={{ maxHeight: '200px', overflow: 'auto' }} />;
    return (
      <div className={classes(wrapper, inputPadding)}>
        <img src={getIconForParameterType('datetime')} className={typeIcon} alt={''}/>
        <SingleSelectDropdown
          label={''}
          list={list}
          onChange={handleBooleanChange}
          value={props.value}
          type={DropdownButtonType.Outline}
          style={iconPadding}
        >
          {props.value === undefined ?
            getFlowParameterInputPlaceholder(props.parameter.domain.type) :
            props.value === 'true' ? Messages.flowParametersInputBooleanTrue() : Messages.flowParametersInputBooleanFalse()
          }
        </SingleSelectDropdown>
      </div>
    );
  }

  function renderDateTimeInput() {
    return (
      <div className={classes(wrapper, inputPadding)}>
        <img src={getIconForParameterType('datetime')} className={typeIcon} alt={''}/>
        <DateInputField
          displayFormat={DateFormat.ShortDateMediumTimeWithFullYear}
          dateInputFieldType={DateInputFieldType.Outline}
          label={''}
          onChange={handleDateChange}
          onConfirm={handleDateChange}
          selectedDate={memoizedDate}
          style={{ width: '100%' }}
          zIndex={1}
        />
      </div>
    );
  }

  function renderDateInput() {
    return (
      <div className={classes(wrapper, inputPadding)}>
        <img src={getIconForParameterType('date')} className={typeIcon} alt={''}/>
        <DateInputField
          dateInputFieldType={DateInputFieldType.Outline}
          label={''}
          onChange={handleDateChange}
          onConfirm={handleDateChange}
          selectedDate={memoizedDate}
          style={{ width: '100%' }}
          zIndex={1}
        />
      </div>
    );
  }

  function renderFloatInput() {
    const valid = (!isNaN(Number(props.value)) && !isNaN(parseFloat(props.value))) ? undefined : false; // Undefined is "true" to show the default styling 
    const validationMessage = valid === undefined ? <br/> : Messages.flowParametersInputNumberValue();
    return (
      <div className={wrapper}>
        <img src={getIconForParameterType('real')} className={typeIcon} alt={''}/>
        <TextFieldWidget
          valid={valid}
          message={validationMessage}
          label={''}
          testId={'FlowParameterInputTestId-' + props.parameter.id}
          onChange={handleValueChange}
          value={props.value || ''}
          style={{ ...iconPadding, width: '100%' }}
          required={props.parameter.isRequired}
          placeholder={getFlowParameterInputPlaceholder(props.parameter.domain.type)}
        />
      </div>
    );
  }

  function renderIntegerInput() {
    const valid = (Number.isInteger(Number(props.value)) && !isNaN(parseFloat(props.value))) ? undefined : false; // Undefined is "true" to show the default styling 
    const validationMessage = valid === undefined ? <br/> : Messages.flowParametersInputIntegerValue();
    return (
      <div className={wrapper}>
        <img src={getIconForParameterType('integer')} className={typeIcon} alt={''}/>
        <TextFieldWidget
          valid={valid}
          message={validationMessage}
          label={''}
          testId={'FlowParameterInputTestId-' + props.parameter.id}
          onChange={handleValueChange}
          value={props.value || ''}
          style={{ ...iconPadding, width: '100%' }}
          required={props.parameter.isRequired}
          placeholder={getFlowParameterInputPlaceholder(props.parameter.domain.type)}
        />
      </div>
    );
  }

  function renderTextInput() {
    return (
      <div className={wrapper}>
        <img src={getIconForParameterType('string')} className={typeIcon} alt={''}/>
        <TextFieldWidget
          label={''}
          testId={'FlowParameterInputTestId-' + props.parameter.id}
          onChange={handleValueChange}
          value={props.value || ''}
          style={{ ...iconPadding, width: '100%' }}
          required={props.parameter.isRequired}
          placeholder={getFlowParameterInputPlaceholder(props.parameter.domain.type)}
        />
      </div>
    );
  }

  function onListSelect(value: string) {
    props.updateValue(props.parameter.id, value);
  }

  function renderListInput() {
    const items: ListItem[] = props.parameter.domain.values.map(value => {
      return { value };
    });
    return (
      <div className={wrapper}>
        <img src={getIconForParameterType(props.parameter.domain.type)} className={typeIcon} alt={''}/>
        <SingleSelectDropdown
          label={''}
          list={<LocallyFilteringList items={items} />}
          type={DropdownButtonType.Outline}
          onChange={onListSelect}
          value={props.value}
          style={{ ...iconPadding }}
        />
      </div>
    );
  }

  if (props.parameter.domain.domainType === 'list') {
    return renderListInput();
  }

  switch(props.parameter.domain.type) {
    case 'boolean':
      return renderBooleanInput();
    case 'real':
      return renderFloatInput();
    case 'integer':
      return renderIntegerInput();
    case 'date':
      return renderDateInput();
    case 'datetime':
      return renderDateTimeInput();
    case 'string':
      return renderTextInput();
    default:
      return renderTextInput();
  }
}
